import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Button, Space, Card, Spin, message, Tabs, DatePicker, Modal, Form, InputNumber, Select, Collapse, Timeline, Input, Radio, Tag, Popconfirm } from 'antd';
import { CalendarOutlined, FilePdfOutlined, EditOutlined, InboxOutlined, UndoOutlined } from '@ant-design/icons';
import api from '../api/client';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useAuth } from '../store/auth';

const { Title, Text } = Typography;

const MEETING_TYPES = [
  { value: "operative", label: "Оперативное совещание по проекту (оперативка)" },
  { value: "gencontractor", label: "Планёрка генподрядчика" },
  { value: "schedule", label: "Совещание по срокам и графику (schedule meeting)" },
  { value: "budget", label: "Совещание по бюджету и затратам" },
  { value: "procurement", label: "Совещание по закупкам и тендерам" },
  { value: "design", label: "Совещание по проектированию" },
  { value: "sales", label: "Совещание по продажам и маркетингу" },
  { value: "development", label: "Совещание по земельному банку и девелопменту" },
  { value: "approuals", label: "Совещание по согласованиям и разрешениям" },
  { value: "quality", label: "Совещание по качеству и техническому надзору" },
  { value: "safety", label: "Совещание по охране труда и промышленной безопасности (ОТ и ПБ)" },
  { value: "commissioning", label: "Совещание по вводу в эксплуатацию и передаче ключей" },
  { value: "legal", label: "Совещание по юридическим вопросам и претензионной работе" },
  { value: "strategic", label: "Стратегическая сессия / совещание совета директоров" },
  { value: "digital", label: "Совещание по цифровизации и BIM" }
];

export default function RegularMeetingDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManageMeetings = user?.special_role === 'secretary' || user?.role_level >= 5 || user?.username === 'khaminov';
  const [meeting, setMeeting] = useState(null);
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [planForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [users, setUsers] = useState([]);
  
  const [chronology, setChronology] = useState([]);
  const [chronoSearch, setChronologySearch] = useState('');
  
  const [crossTasks, setCrossTasks] = useState([]);
  const [crossSearch, setCrossSearch] = useState('');
  
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [pdfForm] = Form.useForm();

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [meetingRes, instancesRes, usersRes, chronoRes, crossRes] = await Promise.all([
        api.get(`/regular-meetings`), // filter on client or create endpoint
        api.get(`/meetings`),
        api.get(`/auth/users`),
        api.get(`/regular-meetings/${id}/chronology`),
        api.get(`/regular-meetings/${id}/cross-tasks`)
      ]);
      const current = meetingRes.data.find(m => m.id === parseInt(id));
      setMeeting(current);
      setInstances(instancesRes.data.filter(m => m.regular_meeting_id === parseInt(id)));
      setUsers(usersRes.data);
      setChronology(chronoRes.data?.groups || []);
      setCrossTasks(crossRes.data?.tasks || []);
    } catch (err) {
      message.error("Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  };

  const handlePlanMeeting = async (values) => {
    try {
      const startTime = values.start_date;
      const endTime = startTime.add(values.duration, 'minute');
      
      await api.post('/meetings', {
        title: MEETING_TYPES.find(t => t.value === meeting.meeting_type)?.label || meeting.meeting_type,
        regular_meeting_id: meeting.id,
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        participant_ids: meeting.participant_ids,
        secretary_id: meeting.secretary_id
      });
      message.success('Встреча запланирована');
      setIsPlanModalOpen(false);
      planForm.resetFields();
      fetchData();
    } catch (err) {
      if (err.response?.status === 409) {
        const conflictMsg = err.response?.data?.detail || 'Время занято у участников. Выберите другое время.';
        message.error(conflictMsg);
      } else {
        message.error('Ошибка планирования встречи');
      }
    }
  };

  const handleEditMeeting = async (values) => {
    try {
      await api.patch(`/regular-meetings/${meeting.id}`, {
        secretary_id: values.secretary_id,
        participant_ids: values.participant_ids
      });
      message.success('Настройки совещания обновлены');
      setIsEditModalOpen(false);
      fetchData();
    } catch (err) {
      message.error('Ошибка при обновлении');
    }
  };

  const openEditModal = () => {
    editForm.setFieldsValue({
      secretary_id: meeting.secretary_id,
      participant_ids: meeting.participant_ids
    });
    setIsEditModalOpen(true);
  };

  const handleArchiveInstance = async (instanceId) => {
    try {
      await api.post(`/meetings/${instanceId}/archive`);
      message.success('Совещание архивировано');
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.detail || 'Ошибка архивирования');
    }
  };

  const handleUnarchiveInstance = async (instanceId) => {
    try {
      await api.post(`/meetings/${instanceId}/unarchive`);
      message.success('Совещание восстановлено');
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.detail || 'Ошибка восстановления');
    }
  };

  const [exportPdfLoading, setExportPdfLoading] = useState(false);

  const handleExportPdf = async (values) => {
    const { entity_type, entity_id } = values;
    
    let groupsToExport = [];
    let titleStr = '';
    let fileName = '';

    if (entity_type === 'all_tasks') {
      groupsToExport = chronology.filter(g => g.entity_type === 'task');
      titleStr = 'Все задачи';
      fileName = 'chronology_all_tasks.pdf';
    } else if (entity_type === 'project') {
      const projGroup = chronology.find(g => g.entity_type === 'project' && g.entity_id === entity_id);
      const taskGroups = chronology.filter(g => g.entity_type === 'task' && g.project_id === entity_id);
      if (projGroup) groupsToExport.push(projGroup);
      groupsToExport.push(...taskGroups);
      
      const projName = projGroup?.title || taskGroups[0]?.project_name || `Проект #${entity_id}`;
      titleStr = projName;
      fileName = `chronology_project_${entity_id}.pdf`;
    } else {
      const taskGroup = chronology.find(g => g.entity_type === 'task' && g.entity_id === entity_id);
      if (taskGroup) groupsToExport.push(taskGroup);
      titleStr = taskGroup?.title || `Задача #${entity_id}`;
      fileName = `chronology_task_${entity_id}.pdf`;
    }
    
    if (groupsToExport.length === 0) {
      message.warning('Нет данных для экспорта');
      return;
    }

    setExportPdfLoading(true);
    message.loading({ content: 'Генерация PDF...', key: 'pdf-gen' });

    try {
      // Create a temporary div to render the content for PDF
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '-9999px';
      tempDiv.style.width = '800px'; // Fixed width for A4 proportion
      tempDiv.style.padding = '40px';
      tempDiv.style.backgroundColor = 'white';
      tempDiv.style.fontFamily = 'sans-serif';
      
      let htmlContent = `
        <h1 style="font-size: 24px; margin-bottom: 20px; color: #000;">Хронология решений: ${titleStr}</h1>
      `;

      groupsToExport.forEach(group => {
        htmlContent += `
          <h2 style="font-size: 18px; margin-top: 20px; margin-bottom: 10px; color: #004080; border-bottom: 2px solid #004080; padding-bottom: 5px;">
            ${group.entity_type === 'project' ? 'Общие решения по проекту' : `Задача: ${group.title}`}
          </h2>
        `;
        group.decisions.forEach(dec => {
          const dateStr = dec.meeting_date ? new Date(dec.meeting_date).toLocaleString() : 'Н/Д';
          htmlContent += `
            <div style="margin-bottom: 15px; border-bottom: 1px solid #eee; padding-bottom: 15px;">
              <div style="font-weight: bold; font-size: 16px; margin-bottom: 8px; color: #333;">Дата: ${dateStr}</div>
              <div style="font-size: 14px; color: #555; white-space: pre-wrap;">${dec.decision_text || ''}</div>
            </div>
          `;
        });
      });

      tempDiv.innerHTML = htmlContent;
      document.body.appendChild(tempDiv);

      const canvas = await html2canvas(tempDiv, { scale: 2 });
      document.body.removeChild(tempDiv);

      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      
      const doc = new jsPDF({ orientation: 'portrait', unit: 'px', format: 'a4' });
      const pdfWidth = doc.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      doc.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      doc.save(fileName);
      
      message.success({ content: 'PDF успешно сгенерирован!', key: 'pdf-gen' });
      setIsPdfModalOpen(false);
    } catch (error) {
      console.error('PDF Generation Error:', error);
      message.error({ content: 'Ошибка при генерации PDF', key: 'pdf-gen' });
    } finally {
      setExportPdfLoading(false);
    }
  };

  const filteredChronology = chronology.filter(g => 
    g.title.toLowerCase().includes(chronoSearch.toLowerCase()) || 
    g.entity_type.toLowerCase().includes(chronoSearch.toLowerCase())
  );

  const filteredCrossTasks = crossTasks.filter(t => 
    t.task_title.toLowerCase().includes(crossSearch.toLowerCase())
  );

  if (loading) return <Spin style={{ display: 'block', margin: '50px auto' }} />;
  if (!meeting) return <div>Не найдено</div>;

  const activeInstances = instances.filter(i => !i.is_archived);
  const archivedInstances = instances.filter(i => i.is_archived);

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 className="page-title">{MEETING_TYPES.find(t => t.value === meeting.meeting_type)?.label || meeting.meeting_type}</h1>
        <Space>
          <Button icon={<EditOutlined />} onClick={openEditModal}>Настройки</Button>
          <Button icon={<FilePdfOutlined />} onClick={() => setIsPdfModalOpen(true)}>PDF</Button>
          <Button 
            type="primary" 
            icon={<CalendarOutlined />} 
            onClick={() => setIsPlanModalOpen(true)}
            style={{ 
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              border: 'none',
              boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
            }}
          >
            Запланировать
          </Button>
        </Space>
      </div>

      <Tabs defaultActiveKey="1" items={[
        {
          key: '1',
          label: `Предстоящие / Прошедшие встречи (${activeInstances.length})`,
          children: (
            <Space direction="vertical" style={{ width: '100%' }}>
              {activeInstances.map(inst => (
                <Card key={inst.id} size="small" title={`Встреча ${new Date(inst.scheduled_start).toLocaleString()}`} extra={
                  <Space>
                    {canManageMeetings && (
                      <Popconfirm title="Архивировать совещание?" onConfirm={() => handleArchiveInstance(inst.id)}>
                        <Button danger size="small" icon={<InboxOutlined />}>В архив</Button>
                      </Popconfirm>
                    )}
                    <Button size="small" onClick={() => navigate(`/meetings/${inst.id}`)}>Перейти к повестке</Button>
                  </Space>
                }>
                  <Text>Статус: {inst.started_at ? (inst.ended_at ? 'Завершена' : 'Идет') : 'Запланирована'}</Text>
                </Card>
              ))}
              {activeInstances.length === 0 && <Text type="secondary">Нет запланированных встреч</Text>}
            </Space>
          )
        },
        {
          key: '4',
          label: `Архив (${archivedInstances.length})`,
          children: (
            <Space direction="vertical" style={{ width: '100%' }}>
              {archivedInstances.map(inst => (
                <Card key={inst.id} size="small" title={`Встреча ${new Date(inst.scheduled_start).toLocaleString()}`} extra={
                  <Space>
                    {canManageMeetings && (
                      <Popconfirm title="Восстановить совещание?" onConfirm={() => handleUnarchiveInstance(inst.id)}>
                        <Button size="small" icon={<UndoOutlined />}>Восстановить</Button>
                      </Popconfirm>
                    )}
                    <Button size="small" onClick={() => navigate(`/meetings/${inst.id}`)}>Перейти к повестке</Button>
                  </Space>
                }>
                  <Tag color="default">Архив</Tag>
                  <Text> Статус: {inst.started_at ? (inst.ended_at ? 'Завершена' : 'Идет') : 'Запланирована'}</Text>
                </Card>
              ))}
              {archivedInstances.length === 0 && <Text type="secondary">Нет архивных встреч</Text>}
            </Space>
          )
        },
        {
          key: '2',
          label: 'Хронология решений',
          children: (
            <Space direction="vertical" style={{ width: '100%' }}>
              <Input.Search 
                placeholder="Поиск по задачам и проектам" 
                allowClear 
                onChange={(e) => setChronologySearch(e.target.value)} 
                style={{ width: 300, marginBottom: 16 }}
              />
              {filteredChronology.length > 0 ? (
                <Collapse 
                  items={filteredChronology.map(group => ({
                    key: `${group.entity_type}_${group.entity_id}`,
                    label: `${group.entity_type === 'project' ? 'Проект' : 'Задача'} #${group.entity_id}: ${group.title}`,
                    children: (
                      <Timeline
                        items={group.decisions.map(dec => ({
                          color: 'blue',
                          children: (
                            <>
                              <Text strong>{dec.meeting_date ? new Date(dec.meeting_date).toLocaleString() : 'Дата неизвестна'}</Text>
                              <div style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
                                {dec.decision_text}
                              </div>
                            </>
                          )
                        }))}
                      />
                    )
                  }))}
                />
              ) : (
                <Text type="secondary">Нет опубликованных решений</Text>
              )}
            </Space>
          )
        },
        {
          key: '3',
          label: 'Кросс-задачи (в других совещаниях)',
          children: (
            <Space direction="vertical" style={{ width: '100%' }}>
              <Input.Search 
                placeholder="Поиск по кросс-задачам" 
                allowClear 
                onChange={(e) => setCrossSearch(e.target.value)} 
                style={{ width: 300, marginBottom: 16 }}
              />
              {filteredCrossTasks.length > 0 ? (
                <Collapse 
                  items={filteredCrossTasks.map(group => ({
                    key: `cross_task_${group.task_id}`,
                    label: `Задача #${group.task_id}: ${group.task_title}`,
                    children: (
                      <Timeline
                        items={group.decisions.map(dec => {
                          const meetingTypeLabel = MEETING_TYPES.find(t => t.value === dec.regular_meeting_type)?.label || dec.regular_meeting_type || 'Разовое совещание';
                          return {
                            color: dec.regular_meeting_id === parseInt(id) ? 'green' : 'blue',
                            children: (
                              <>
                                <Space style={{ marginBottom: 8 }}>
                                  <Tag color={dec.regular_meeting_id === parseInt(id) ? 'green' : 'blue'}>
                                    {meetingTypeLabel}
                                  </Tag>
                                  <Text strong>{dec.meeting_date ? new Date(dec.meeting_date).toLocaleString() : 'Дата неизвестна'}</Text>
                                </Space>
                                <div style={{ whiteSpace: 'pre-wrap' }}>
                                  {dec.decision_text}
                                </div>
                              </>
                            )
                          };
                        })}
                      />
                    )
                  }))}
                />
              ) : (
                <Text type="secondary">Нет задач, пересекающихся с другими регулярными совещаниями</Text>
              )}
            </Space>
          )
        }
      ]} />

      <Modal
        title="Запланировать встречу"
        open={isPlanModalOpen}
        onCancel={() => setIsPlanModalOpen(false)}
        onOk={() => planForm.submit()}
      >
        <Form form={planForm} layout="vertical" onFinish={handlePlanMeeting} initialValues={{ duration: 60 }}>
          <Form.Item name="start_date" label="Дата и время начала" rules={[{ required: true, message: 'Выберите дату и время' }]}>
            <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="duration" label="Продолжительность (минут)" rules={[{ required: true }]}>
            <InputNumber min={15} max={480} step={15} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Настройки совещания"
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        onOk={() => editForm.submit()}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditMeeting}>
          <Form.Item name="secretary_id" label="Секретарь" rules={[{ required: true }]}>
            <Select
              showSearch optionFilterProp="label" filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())} options={users.map(u => ({ value: u.id, label: u.full_name }))}
            />
          </Form.Item>
          <Form.Item name="participant_ids" label="Участники" rules={[{ required: true }]}>
            <Select
              mode="multiple"
              showSearch optionFilterProp="label" filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())} options={users.map(u => ({ value: u.id, label: u.full_name }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Экспорт хронологии"
        open={isPdfModalOpen}
        onCancel={() => setIsPdfModalOpen(false)}
        onOk={() => pdfForm.submit()}
        okText="Сформировать PDF"
        cancelText="Отмена"
      >
        <Form form={pdfForm} layout="vertical" onFinish={handleExportPdf}>
          <Form.Item name="entity_type" label="Что экспортируем?" rules={[{ required: true, message: 'Выберите тип' }]}>
            <Radio.Group onChange={() => pdfForm.setFieldsValue({ entity_id: null })}>
              <Radio.Button value="all_tasks">Все задачи</Radio.Button>
              <Radio.Button value="project">Проект</Radio.Button>
              <Radio.Button value="task">Отдельная задача</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item 
            noStyle 
            shouldUpdate={(prevValues, currentValues) => prevValues.entity_type !== currentValues.entity_type}
          >
            {({ getFieldValue }) => {
              const entityType = getFieldValue('entity_type');
              if (!entityType || entityType === 'all_tasks') return null;
              
              const optionsMap = new Map();
              if (entityType === 'project') {
                chronology.forEach(g => {
                  if (g.entity_type === 'project') {
                    optionsMap.set(g.entity_id, { value: g.entity_id, label: g.title });
                  } else if (g.entity_type === 'task' && g.project_id) {
                    optionsMap.set(g.project_id, { value: g.project_id, label: g.project_name || `Проект #${g.project_id}` });
                  }
                });
              } else if (entityType === 'task') {
                chronology.forEach(g => {
                  if (g.entity_type === 'task') {
                    optionsMap.set(g.entity_id, { value: g.entity_id, label: g.title });
                  }
                });
              }
              
              const options = Array.from(optionsMap.values());
                
              return (
                <Form.Item name="entity_id" label="Выберите сущность" rules={[{ required: true, message: 'Сделайте выбор' }]}>
                  <Select
                    showSearch
                    placeholder="Поиск..."
                    optionFilterProp="label"
                    options={options}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
